import { Status } from '../../shared/models/models';
import { RulesConfig } from '../../shared/models/rules.models';
import { ImageResizeType } from '../settings/models/settings-models';
import { AbTestingType, FormField } from './experiences';
import { RemarketingConfig } from './remarketing.models';

export type UnitUiTypeName = 'basic' | 'form' | 'recommend' | 'script' | 'hub' | 'survey' | 'fastext' | 'contexTile';
export type CloseButtonShape = 'square' | 'round' | 'no';
export type CloseButtonPosition = 'inside' | 'outside';

export enum UnitType {
  Campaign = 1,
  Offer,
  PreStyle,
  Autopilot,
  ContexTile
}

export enum CampaignCategory {
  General = 1,
  Ace,
  Remarketing,
  ContentHubs,
  ContexTile
}

export interface ExperienceGeneral {
  name: string;
  unitId?: number;
  status: Status;
  createdDate: string;
}

export interface ExperienceTheme {
  theme: Theme;
  secondaryTextColor: any;
  backgroundColor?: string;
  textColor?: string;
  linkColor?: string;
  inputTextColor?: string;
  inputBackgroundColor?: string;
  inputBorderColor?: string;
  successTextColor?: string;
  buttonBackgroundColor?: string;
  buttonTextColor?: string;
  css?: string;
}

export interface ExperienceCreativeBasic extends ExperienceTheme {
  name: string;
  uiType: UnitUiTypeName;
  layoutId: number;
  libPreStyleId?: number;
  visibilityType?: UnitVisibilityType;
  screenPosition?: number;
  classes: string[];
  customHtml: string;
  height: string;
  width: string;
  // text
  buttonText: string;
  title: string;
  subTitle: string;
  successText: string;
  // text size
  buttonTextSize: number;
  titleSize: number;
  subTitleSize: number;
  successTextSize: number;
  // links
  imgUrl?: string;
  isOpenInNewWindow: boolean;
  isUnitLink: boolean; // wheather the entire unit is clickable
  // form
  scriptCode?: string;
  formFields: FormField[];
  maProvider?: number;
  maProviderListToSendLeads?: string;
  maFormReference?: string;
  emailNotificationList?: string;
  emailNotificationEnabled: boolean;
  // recommend
  recommendationsLimit: number;
  showRecommendTags?: boolean;
  showRecommendArea?: boolean;
  recommendIsCarousel?: boolean;
  recommendCarouselDisplayLimit?: number;
  recommendCarouselShowIndicator?: boolean;
  recommendCarouselIndicatorColor?: string;
  // survey
  surveyQuestions?: SurveyQuestion[];
  videoScript?: string;
  closeButton?: CustomCloseButton;
  // ai
  aiCreativeFirstResponseId: string;
  // contexTiles
  showPageSummary?: boolean;
  showChat?: boolean;
  showTitleInCollapsedBar?: boolean;
  placeholderText?: string;
  disclamerText?: string;
  guidingQuestions?: GuidingQuestionsConfig | null;
  ctaButtonConfig?: CtaButtonConfig | null;
  showPanelPosition?: boolean;
  qnaBarContentType?: QnaBarContentType;
  launchButton?: LaunchButton;
  launchMobile?: LaunchMobile;
}

export interface GuidingQuestionsConfig {
  type?: GuidingQuestionsType;
  title?: string | null;
  color?: string;
  questionsLimit: number;
}

export enum GuidingQuestionsType {
  FullQuestions = 0,
  Chips = 1,
}

export interface CtaButtonConfig {
  text: string;
  link: string;
  textColor: string;
  backgroundColor: string;
}

export interface CustomCloseButton {
  shape: CloseButtonShape;
  color: string;
  position: CloseButtonPosition;
  background: string;
  border: string;
  size: string;
}

export interface LaunchButton {
  text: string;
  textColor: string;
  backgroundColor: string;
  iconUrl: string;
  screenPosition: number;
  horizontalOffset: number;
  verticalOffset: number;
}

export interface LaunchMobile {
  triggerStyle: LauncherTriggerStyle;
}

export enum QnaBarContentType {
  ChatOnly = 1,
  GuidingQuestionsOnly = 2,
  ChatAndGuidingQuestions = 3,
}

export enum LaunchMobileTrigger {
  PageLoad = 1,
  Scroll = 2,
  Timer = 3,
}

export enum LauncherTriggerStyle {
  Button = 1,
  Bar = 2,
}

export interface ExperienceCreative extends ExperienceCreativeBasic {
  preStyleId?: number;
  link: string;
  contentPoolId?: number;
  recommendUrlParams?: string;
}

export interface ExperienceSchedule {
  fromDate?: string;
  toDate?: string;
}

export interface ExperienceTriggers {
  triggers: string[];
  scrollDepth?: number;
}

export interface ExperienceDevice {
  deviceFilter?: RulesConfig;
}

export interface ExperienceEmbedded {
  guid?: string;
  groupId?: number;
}

export interface ExperienceTargets extends ExperienceDevice {
  audienceFilter?: RulesConfig;
  urlFilter?: RulesConfig;
  maFilter?: RulesConfig;
  predefinedAudiences?: number[];
  segmentIds?: number[];
}

export interface ExperienceUnit extends ExperienceGeneral, ExperienceCreative, ExperienceTargets, ExperienceSchedule, ExperienceTriggers, ExperienceEmbedded {
  tags?: string[];
  exposureRate: number;
  abTestingPairId?: number;
  abTestingPairIdPrv?: number;
  abTestingEnabled: boolean;
  abTestingType: AbTestingType;
  abTestExposureRate: number;
  abTestExposureRatePrv: number;
  postCompletionScript?: string;
  postCompletionConfig?: PostCompletionConfig;
  displayFrequency?: DisplayFrequencyConfig;
  isCompletionAgnostic: boolean;
}

export interface ExperienceRemarketing extends ExperienceGeneral, ExperienceTargets, ExperienceDevice, ExperienceSchedule {
  guid: string;
  displayFrequency?: DisplayFrequencyConfig;
  remarketingConfig: RemarketingConfig;
  uiType: 'script';
  visibilityType: 'none';
  layoutId: 2000000;
  classes: ['none'];
}

export interface ExperienceOffer extends ExperienceGeneral, ExperienceCreative, ExperienceSchedule, ExperienceTriggers, ExperienceDevice, ExperienceEmbedded {
  gatedOffer: boolean;
  offerFunnelPosition: OfferFunnelPosition;
  offerTagIds: number[];
}

export interface ExperiencePreStyle extends ExperienceGeneral, ExperienceCreativeBasic {
  thumbnailUrl: string;
}

export interface ExperienceUnitListItem extends ExperienceGeneral {
  unitType: UnitType;
  guid: string;
  order: number;
  visibilityType: UnitVisibilityType;
  triggers: string[];
  uiType: UnitUiTypeName;
}

export interface DisplayFrequencyConfig {
  displayAgain: DisplayAgain;
}

export interface DisplayAgain {
  frequency: Frequency;
}

export enum Frequency {
  EveryPage = 0,
  EverySession = 1,
  Never = 3
}

export interface PostCompletionConfig {
  postCompletionType: PostCompletionType;
  goalId?: number;
  url?: string;
}

export enum PostCompletionType {
  None = 0,
  GoalPixel = 1,
  DownloadFile = 2,
  Redirect = 3
}

export enum OfferFunnelPosition {
  Automatic = 1,
  Top,
  Middle,
  Bottom
}

export interface ExperienceUnitImage {
  imgFile?: File;
  resizeType?: ImageResizeType;
  isReturnFullUrl?: boolean;
}

export interface ExperienceUnitGroup {
  id?: number;
  name: string;
  groupType: UnitGroupType;
  selector?: string;
  pageRuleType?: UnitGroupPageRuleType;
  areaIds?: number[];
  pageTagIds?: number[];
  urls?: string[];
  placementRules?: UnitGroupPlacementRule[];
}

export interface ExperienceUnitGroupListItem extends ExperienceUnitGroup {
  usageCount: number;
}

export enum UnitGroupType {
  Manual = 1,
  AutoEmbed,
}

export enum UnitGroupPageRuleType {
  Areas = 1,
  PageTags,
  Urls,
}

export type UnitGroupRuleType = 'AfterParagraph' | 'beforeAfterSelector' | 'startEndContainer';

export interface UnitGroupPlacementRule {
  placementType: UnitGroupRuleType;
  params: string;
}

export type Theme = 'trendemon' | 'light' | 'dark' | 'rich' | 'vivid' | 'purple' | 'custom' | string;
export type UnitVisibilityType = 'light' | 'full' | 'slider' | 'launcher' | 'fixed' | 'embedded' | 'none';

export enum UnitUiTypeId {
  Basic = 1,
  Recommend,
  Form,
  Script,
  Hub,
  Survey,
  Fastext,
  ContexTile
}

export enum UnitScreenPosition {
  TopLeft = 0,
  Top = 1,
  TopRight = 2,
  Left = 3,
  Center = 4,
  Right = 5,
  BottomLeft = 6,
  Bottom = 7,
  BottomRight = 8
}

export interface ThemeSpec {
  name: Theme;
  backgroundColor: string;
  textColor: string;
  linkColor?: string;
  inputTextColor?: string;
  inputBackgroundColor?: string;
  inputBorderColor?: string;
  successTextColor?: string;
  buttonBackgroundColor: string;
  buttonTextColor: string;
  secondaryTextColor?: string;
  carouselIndicatorColor?: string;
  css?: string;
  titleSize?: number;
  subTitleSize?: number;
  successTextSize?: number;
  buttonTextSize?: number;
}

export interface UnitLead {
  clientUrl: string;
  countryCode: string | null;
  countryName: string | null;
  createdAt: Date;
  leadIdentity: string;
  leadId: string;
  name: string;
  leadIp: string;
  leadDetails?: UnitLeaField[];
}
export interface UnitLeaField {
  name: string;
  value: string;
}

export interface PersonalUnit {
  id: number;
  orchestrationId: number;
  name: string;
  visibilityType: UnitVisibilityType;
  typeId: PersonaUnitTypeId;
}

export enum PersonaUnitTypeId {
  Text,
  Content,
  Form,
  Script,
  Hub,
  Survey
}

export interface SurveyQuestion {
  questionId: number;
  question: string;
  answers: string[];
}


export interface SurveyQuestionForAudience {
  key: string;
  fullName: string;
  surveyName: string;
  surveyId: number;
}

export interface Orchestration {
  id: number;
  name: string;
  status: Status;
  createdDate: string;
  isAllVisitors: boolean;
  audienceIds: number[];
  audienceFilter?: RulesConfig;
  segmentIds: number[];
  maFilter?: RulesConfig;
  goalIds: number[];
  offerTagIds: number[];
  areaIds: number[];
  pageTagIds: number[];
  urlFilter?: RulesConfig;
}

export interface OrchestrationAccountConfig {
  showIntro: boolean;
  hasActiveGoals: boolean;
  hasOffers: boolean;
}

export interface ConversationRule extends AddConversationRule {
  id: number;
  accountId: number;
  status: Status;
  createDate: string;
  lastUpdatedBy?: string;
}

export interface AddConversationRule {
  name: string;
  ruleText: string;
}

export interface ExperienceContexTiles extends ExperienceGeneral, ExperienceCreativeBasic, ExperienceTriggers, ExperienceDevice, ExperienceEmbedded {
  triggerStyle: LauncherTriggerStyle;
}

export interface ContexTilesRule extends ExperienceUnitListItem, ExperienceTriggers, ExperienceSchedule, ExperienceDevice {
}


export interface ContexTilesPage extends PageGetOrScanRequest, PageGetOrScanResponse {
}

export interface PageGetOrScanRequest {
  url: string;
}

export interface PageGetOrScanResponse {
  title?: string;
  description?: string;
  imageUrl?: string;
  imageLink?: string;
  uploadImage?: File;
  summary?: string;
}

export interface ContexTilesRunPromptRequest {
  pageUrl: string;
  prompt: string;
  prevResponseId: string;
}

export interface ContexTilesRunPromptResponse {
  responseId: string;
  response: string;
  error: string;
  pagesContent: string;
}
